import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { mgcIconDownRegular } from '@sb/mgc-icons/mgcIcon-down_regular.icon';

import { IconRegistry } from '../icon';
import { IconComponent } from '../icon/icon.component';
import { ACCORDION_PANEL, AccordionPanelComponent } from './accordion-panel.component';
import { ACCORDION, AccordionComponent } from './accordion.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-accordion-header',
  imports: [IconComponent],
  templateUrl: './accordion-header.component.html',
  styleUrls: ['./accordion-header.component.scss'],
  host: {
    tabindex: '0',
    role: 'button',
    '[attr.aria-disabled]': 'disabled() || false',
    '[attr.aria-expanded]': 'expanded() || false',
    '[attr.aria-controls]': 'contentId() || ""',
    '[id]': 'headerId() || ""',
    '(click)': 'onClick()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class AccordionHeaderComponent {
  private parentPanel = inject<AccordionPanelComponent>(ACCORDION_PANEL, { optional: true, skipSelf: true });
  private parentAccordion = inject<AccordionComponent>(ACCORDION, { optional: true, skipSelf: true });

  toggleable = computed(() => this.parentPanel?.toggleable() ?? true);
  disabled = computed(() => this.parentPanel?.disabled() || false);
  expanded = computed(() => this.parentPanel?.expanded() || false);
  contentId = computed(() => this.parentPanel?.contentId() || '');
  headerId = computed(() => this.parentPanel?.headerId() || '');

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconDownRegular]);
  }

  /**
   * Handle click event
   */
  onClick(): void {
    if (this.disabled() || !this.toggleable()) return;

    if (this.parentPanel && this.parentAccordion) {
      this.parentAccordion.togglePanel(this.parentPanel.id());
    } else {
      console.error('AccordionHeaderComponent must be a child of AccordionPanelComponent');
    }
  }

  /**
   * Handle keyboard navigation
   */
  onKeydown(event: KeyboardEvent): void {
    if (!this.parentPanel || !this.parentAccordion) return;
    const panelId = this.parentPanel.id();

    // Toggle on Enter or Space
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.parentAccordion.togglePanel(panelId);
      return;
    }

    // Panel navigation
    switch (event.key) {
      case 'Home':
        event.preventDefault();
        this.parentAccordion.focusFirstPanel();
        break;

      case 'End':
        event.preventDefault();
        this.parentAccordion.focusLastPanel();
        break;

      case 'ArrowUp':
      case 'Up': // For older browsers
        event.preventDefault();
        this.parentAccordion.focusPreviousPanel(panelId);
        break;

      case 'ArrowDown':
      case 'Down': // For older browsers
        event.preventDefault();
        this.parentAccordion.focusNextPanel(panelId);
        break;
    }
  }
}
